import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Users, Plus, Edit, Trash2, Mail, Building, Award, ArrowLeft } from 'lucide-react';

export default function AuthorManagement() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAuthor, setEditingAuthor] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    credentials: '',
    affiliation: '',
    bio: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.role !== 'admin') {
      navigate('/research-library');
      return;
    }
    fetchAuthors();
  }, [user, navigate]);

  const fetchAuthors = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/authors`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setAuthors(data);
    } catch (error) {
      console.error('Error fetching authors:', error);
      setError('Failed to load authors');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const token = localStorage.getItem('access_token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      if (editingAuthor) {
        // Update existing author
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/api/authors/${editingAuthor.id}`,
          formData,
          config
        );
      } else {
        // Create new author
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/authors`,
          formData,
          config
        );
      }

      // Reset form and refresh list
      setFormData({ name: '', email: '', credentials: '', affiliation: '', bio: '' });
      setShowForm(false);
      setEditingAuthor(null);
      fetchAuthors();
    } catch (err) {
      console.error('Submit error:', err);
      setError(err.response?.data?.detail || 'Failed to save author');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (author) => {
    setEditingAuthor(author);
    setFormData({
      name: author.name,
      email: author.email,
      credentials: author.credentials,
      affiliation: author.affiliation,
      bio: author.bio
    });
    setShowForm(true);
  };

  const handleDelete = async (authorId) => {
    if (!window.confirm('Are you sure you want to delete this author?')) return;

    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/authors/${authorId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      fetchAuthors();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete author');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingAuthor(null);
    setFormData({ name: '', email: '', credentials: '', affiliation: '', bio: '' });
    setError('');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-12 text-center">
          <p className="text-gray-500">Loading...</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="container mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/admin')}
          className="mb-6 flex items-center gap-2 text-gray-600 hover:text-[#E87722] transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Dashboard
        </button>

        {/* Page Header */}
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-3">
            <Users className="h-8 w-8 text-[#E87722]" />
            <h1 className="text-3xl font-bold text-[#1E3A5F]">Author Management</h1>
          </div>
          {!showForm && (
            <Button
              onClick={() => setShowForm(true)}
              className="bg-[#E87722] hover:bg-[#d66a1a] text-white"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Author
            </Button>
          )}
        </div>

        {/* Add/Edit Form */}
        {showForm && (
          <Card className="p-6 mb-8">
            <h2 className="text-xl font-bold text-[#1E3A5F] mb-4">
              {editingAuthor ? 'Edit Author' : 'Add New Author'}
            </h2>

            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                {/* Name */}
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Dr. Jane Smith"
                    className="mt-1"
                  />
                </div>

                {/* Email */}
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="jane.smith@university.edu"
                    className="mt-1"
                  />
                </div>

                {/* Credentials */}
                <div>
                  <Label htmlFor="credentials">Credentials *</Label>
                  <Input
                    id="credentials"
                    name="credentials"
                    value={formData.credentials}
                    onChange={handleChange}
                    required
                    placeholder="Ph.D. in Economics"
                    className="mt-1"
                  />
                </div>

                {/* Affiliation */}
                <div>
                  <Label htmlFor="affiliation">Affiliation/Organization *</Label>
                  <Input
                    id="affiliation"
                    name="affiliation"
                    value={formData.affiliation}
                    onChange={handleChange}
                    required
                    placeholder="Harvard University"
                    className="mt-1"
                  />
                </div>

                {/* Bio */}
                <div className="md:col-span-2">
                  <Label htmlFor="bio">Biography *</Label>
                  <textarea
                    id="bio"
                    name="bio"
                    value={formData.bio}
                    onChange={handleChange}
                    required
                    rows={4}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E87722] focus:border-transparent"
                    placeholder="Brief professional biography..."
                  />
                </div>
              </div>

              <div className="flex gap-3 justify-end">
                <Button
                  type="button"
                  onClick={handleCancel}
                  variant="outline"
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-[#E87722] hover:bg-[#d66a1a] text-white"
                >
                  {submitting ? 'Saving...' : editingAuthor ? 'Update Author' : 'Add Author'}
                </Button>
              </div>
            </form>
          </Card>
        )}

        {/* Authors List */}
        <div className="grid gap-4">
          {authors.length === 0 ? (
            <Card className="p-8 text-center">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">No authors yet. Add your first author!</p>
            </Card>
          ) : (
            authors.map((author) => (
              <Card key={author.id} className="p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-[#1E3A5F] mb-2">
                      {author.name}
                    </h3>
                    
                    <div className="space-y-2 mb-3">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Award className="h-4 w-4 text-[#E87722]" />
                        <span>{author.credentials}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <Building className="h-4 w-4 text-[#E87722]" />
                        <span>{author.affiliation}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <Mail className="h-4 w-4 text-[#E87722]" />
                        <span>{author.email}</span>
                      </div>
                    </div>

                    <p className="text-gray-700">{author.bio}</p>
                  </div>

                  <div className="flex gap-2 ml-4">
                    <Button
                      onClick={() => handleEdit(author)}
                      variant="outline"
                      size="sm"
                      className="text-[#1E3A5F] border-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      onClick={() => handleDelete(author.id)}
                      variant="outline"
                      size="sm"
                      className="text-red-600 border-red-600 hover:bg-red-600 hover:text-white"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
